import { useState, useCallback, useEffect } from 'react';
import Link from 'next/link';
import { NextSeo } from 'next-seo';
import clsx from 'clsx';
import { ArrowRightIcon } from '@heroicons/react/20/solid';
import { CubeIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import Image from 'next/image';
import type {
  FagmoblerPublicOmniumProduct,
  Breadcrumbs,
  FagmoblerAlgoliaProduct,
  FagmoblerAlgoliaProductVariant,
  FagmoblerInventory,
} from '@fagmobler/types';
import {
  Alert,
  Button,
  calculatePercentOff,
  ProductCollectionCard,
  ProductListGridItem,
  Promotion,
} from '@fagmobler/ui';
import { Pdf } from '@fagmobler/sanity';

import { usePreviewSubscribtion } from '@/lib/sanity/hooks';
import {
  MaterializedProductEnrichment,
  productEnrichmentPreviewQuery,
} from '@/lib/sanity/queries';
import { fagmoblerSlugify } from '@/lib/utils/getUrls';
import {
  convertProductDataIntoGtagEventParams,
  trackEvent,
} from '@/lib/analytics';

import Layout from '@/components/layout';
import ImageCarousel, { TImage } from '@/components/imageCarousel';
import AddToCart from '@/components/AddToCart/AddToCart';

import { KeyProperties, SizeProperties } from './components/properties';
import { Price } from './components/Price';
import { Tags } from './components/Tags';
import { Stock } from './components/Stock';
import { Variants } from './components/Variants';

import { ProductPageAccordion } from './components/Accordion';
import { ProductPagePdfs } from './components/Pdfs';
import { RelatedProducts } from './components/RelatedProducts';
import { FagmoblerPortableText } from '@/components/portableText';
import { getCanonicalPath } from '@/lib/algolia/getCanonicalPath';
import { ProductPageJsonLd } from './components/JsonLd';
import { useStores } from '@/components/store';
import { ProductPageData } from './getData';
import Debug from '@/components/debug';
import { missingImageUrls } from '@/lib/utils/missingImageUrls';
import { siteUrl } from '@/next-sitemap.config';

type CommonProps = {
  preview: boolean;
  previewData: any | null;
  slugs: string[] | null;
};

export type ProductPageProps = ProductPageData & CommonProps;

export function ProductPage({
  publicOmniumProduct,
  inventory,
  algoliaProduct,
  breadcrumbs,
  algoliaProductVariants,
  previewData,
  preview = false,
  sanityProductEnrichment,
  relatedProducts,
  productsInSerie,
  relatedPdfs,
  motherProductSKU,
  generatedProductData,
}: ProductPageProps) {
  const productEnrichment = usePreviewSubscribtion<
    MaterializedProductEnrichment | undefined
  >({
    query: productEnrichmentPreviewQuery,
    queryParams: { id: previewData?.id },
    preview,
    previewData,
    initialData: sanityProductEnrichment || undefined,
  });
  const { current: currentStoreQuery } = useStores();

  const [selectedVariant, setSelectedVariant] = useState<string | null>(
    publicOmniumProduct.skuId || algoliaProduct?.objectID || null
  );

  // variant means publisertSammen in omnium (published together)
  const handleChangeVariant = useCallback((variant: string) => {
    setSelectedVariant(variant);
  }, []);

  useEffect(() => {
    if (!publicOmniumProduct && !algoliaProduct) return;

    trackEvent(
      'view_item',
      convertProductDataIntoGtagEventParams({
        lineItem: { code: selectedVariant, quantity: 1 },
        omniumProduct: publicOmniumProduct,
        algoliaProduct: algoliaProduct || undefined,
      })
    );
  }, [selectedVariant, publicOmniumProduct, algoliaProduct]);

  if (!publicOmniumProduct) {
    throw new Error('Product not found');
  }

  const omniumAssets =
    publicOmniumProduct.assets?.map((asset) => ({
      url: asset?.url,
      key: asset?.assetId,
    })) || [];

  const images = omniumAssets?.length
    ? (omniumAssets as TImage[])
    : ([
        {
          url: algoliaProduct?.image,
          key: 'mainImage',
        },
      ].filter((image) => image.url) as TImage[]);

  const properties: Record<string, string> =
    publicOmniumProduct.properties.reduce(
      (properties: Record<string, string>, { key, value }) => {
        if (!key || !value) return properties;

        properties[key] = value;

        return properties;
      },
      {}
    );

  const builderLink = properties['3dlink'] ?? null;

  const { serie, posterName, posterDescription, salesInfo } = properties;
  const { tags, price, campaignPrice } = algoliaProduct || {};

  const title = posterName;
  const description = salesInfo || posterDescription;
  if (!algoliaProduct) return null;
  const serieUrl = serie && `/serie/${fagmoblerSlugify(serie)}`;
  const canonicalPath = getCanonicalPath(algoliaProduct);
  const canonicalUrl = canonicalPath && `${siteUrl}${canonicalPath}`;

  const discountInPercent =
    (price && campaignPrice && calculatePercentOff(price, campaignPrice)) || 0;

  const isInStock =
    inventory &&
    currentStoreQuery.data?.store?.id &&
    inventory[publicOmniumProduct.skuId] &&
    inventory[publicOmniumProduct.skuId][currentStoreQuery.data.store.id] &&
    inventory[publicOmniumProduct.skuId][currentStoreQuery.data.store.id]
      .stock > 0;

  const notAvailable = !!publicOmniumProduct.isBackorder && !isInStock;

  return (
    <Layout breadcrumbs={breadcrumbs || undefined} isPreview={preview}>
      <ProductPageJsonLd
        algoliaProduct={algoliaProduct}
        omniumProduct={publicOmniumProduct}
        canonicalUrl={canonicalUrl}
      />
      <NextSeo
        title={algoliaProduct.posterName}
        canonical={canonicalUrl}
        description={
          publicOmniumProduct.description || algoliaProduct.posterDescription
        }
        openGraph={{
          type: 'website',
          url: canonicalUrl,
          title,
          description,
          images: [
            {
              url: images[0].url,
              width: 800,
              height: 600,
              alt: title,
            },
          ],
        }}
      />
      <div className="md:grid md:grid-cols-12 gap-8 mt-5 print:!block">
        {images && images.length > 0 && (
          <div className="md:col-span-6 rounded-lg relative">
            <div className="md:sticky md:top-16 print:!static">
              <ImageCarousel
                images={images}
                alt={[title || algoliaProduct.posterName || 'Bilde av produkt']}
                mainImageSizes="(max-width: 375px) 273px, (max-width: 640px) calc(100vw - 4rem), (max-width: 1024px) 336px, (max-width: 1256px) 432px, 504px"
              />
              {discountInPercent >= 15 && (
                <Promotion
                  size="lg"
                  className="absolute bottom-32 right-5 md:right-8 lg:right-10 z-10"
                  percent={discountInPercent}
                />
              )}
            </div>
          </div>
        )}

        <div className="mt-5 md:mt-0 md:col-span-6">
          <Tags product={algoliaProduct} />

          <h1 className="hd-xl print:hd-3xl print:mt-0">{posterName}</h1>
          <p className="mb-4 print:hd-base">{posterDescription}</p>

          <div className="mb-5 leading-snug">
            {publicOmniumProduct.description}
          </div>

          {salesInfo && <div className="mb-5 leading-snug">{salesInfo}</div>}

          {serieUrl && (
            <Link href={serieUrl}>
              <Button
                Icon={ArrowRightIcon}
                iconPlacement="right"
                variant="outline"
                className="print:hidden"
              >
                Vis alle fra {serie} serien
              </Button>
            </Link>
          )}

          <div className={clsx('my-4')}>
            <SizeProperties product={publicOmniumProduct} />
          </div>

          {relatedProducts && relatedProducts.length > 0 && (
            <RelatedProducts
              products={[...relatedProducts, algoliaProduct]}
              current={algoliaProduct.objectID}
              basePath={breadcrumbs?.at(motherProductSKU ? -2 : -1)?.path}
            />
          )}

          {algoliaProduct.model &&
            algoliaProductVariants &&
            algoliaProductVariants.length > 1 &&
            selectedVariant && (
              <Variants
                variants={algoliaProductVariants}
                current={selectedVariant}
                onChange={handleChangeVariant}
              />
            )}

          <div className={clsx('mb-8')}>
            <Price product={publicOmniumProduct} />
          </div>
          <Stock
            product={publicOmniumProduct}
            inventory={inventory || undefined}
          >
            {builderLink && (
              <Link href={builderLink} legacyBehavior passHref>
                <Button
                  className={clsx('w-full', 'justify-center', 'mb-2')}
                  variant="outline"
                >
                  <CubeIcon className={clsx('w-4', 'h-4', 'mr-2')} /> Bygg
                  produktet i 3D
                </Button>
              </Link>
            )}
            <AddToCart
              omniumProduct={publicOmniumProduct}
              algoliaProduct={algoliaProduct}
              selectedVariant={selectedVariant || undefined}
              disabled={notAvailable}
            >
              <ShoppingCartIcon className={clsx('w-4', 'h-4', 'mr-2')} /> Legg i
              handlekurv
            </AddToCart>
            {notAvailable && (
              <Alert className="mt-4" type="error">
                Tomt på lager.
              </Alert>
            )}
          </Stock>
        </div>
      </div>

      <div className="space-y-10 mt-10">
        <div className="space-y-2">
          <ProductPageAccordion title="Egenskaper">
            <KeyProperties
              product={publicOmniumProduct}
              generatedProductData={generatedProductData}
            />
          </ProductPageAccordion>
          <div className="print:hidden">
            {relatedPdfs && relatedPdfs.length > 0 && (
              <ProductPageAccordion title="Kataloger">
                <ProductPagePdfs pdfs={relatedPdfs} />
              </ProductPageAccordion>
            )}
          </div>
        </div>
        <div className="print:hidden">
          {productsInSerie && productsInSerie.length > 2 && (
            <ProductCollectionCard
              title={`Mer fra ${serie} serien`}
              href={serieUrl}
              readMoreText={`Vis alle fra ${serie} serien`}
            >
              {productsInSerie.map((item: FagmoblerAlgoliaProduct) => {
                const href = getCanonicalPath(item);
                const src = item.image;
                const hasImage = !!src && !missingImageUrls.includes(src);
                if (!href) return null;
                return (
                  <Link href={href} key={item.objectID} className="block">
                    <ProductListGridItem
                      product={item}
                      image={
                        hasImage && (
                          <Image
                            fill
                            src={src}
                            className="object-contain w-auto h-auto"
                            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 164px, (max-width: 1280px) 166px, 211px"
                            alt={
                              item.posterName
                                ? `$Bilde av ${item.posterName} (${item.posterDescription})`
                                : 'Bilde av produkt'
                            }
                          />
                        )
                      }
                      store={currentStoreQuery?.data?.store}
                    />
                  </Link>
                );
              })}
            </ProductCollectionCard>
          )}
        </div>
      </div>
      <div className="print:hidden">
        {productEnrichment?.body && (
          <FagmoblerPortableText value={productEnrichment.body} />
        )}
      </div>
      <div className="print:hidden">
        <Debug data={publicOmniumProduct} label="Omnium product" />
        <Debug data={inventory || {}} label="Fagmobler Product inventory" />
        <Debug data={algoliaProduct} label="Algolia product" />
        <Debug
          data={algoliaProductVariants || {}}
          label="Algolia product variants"
        />
        <Debug data={productEnrichment} label="Product enrichment" />
        <Debug data={relatedProducts || {}} label="Related products" />
        <Debug data={productsInSerie || {}} label="Products in same serie" />
        <Debug data={relatedPdfs || {}} label="PDFs" />
      </div>
    </Layout>
  );
}

export default ProductPage;
